import { useState, useRef, useEffect } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { X } from "lucide-react";
import axios from "axios";
import Swal from "sweetalert2";

const EditAddressForm = ({ formData, setFormData, onClose, onSubmit }) => {
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(false);

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const formRef = useRef(null);

  // 🔑 Fetch auth token & user id from localStorage
  const authToken = localStorage.getItem("authToken");
  const authUserId = localStorage.getItem("authUserId");

  // 🔄 Fetch Countries on Mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await axios.get(
          "https://code.gurusport.in/api/countries",
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        setCountries(res.data?.data || []);
      } catch (err) {
        console.error("❌ Error fetching countries:", err);
      }
    };
    fetchCountries();
  }, [authToken]);

  // 🔄 Fetch States when Country changes
  useEffect(() => {
    if (!formData.country) return;
    const fetchStates = async () => {
      try {
        const res = await axios.get(
          `https://code.gurusport.in/api/states/${formData.country}`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        setStates(res.data?.data || []);
      } catch (err) {
        console.error("❌ Error fetching states:", err);
      }
    };
    fetchStates();
  }, [formData.country, authToken]);

  // 🔄 Fetch Cities when State changes
  useEffect(() => {
    if (!formData.state) return;
    const fetchCities = async () => {
      try {
        const res = await axios.get(
          `https://code.gurusport.in/api/cities/${formData.state}`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        setCities(res.data?.data || []);
      } catch (err) {
        console.error("❌ Error fetching cities:", err);
      }
    };
    fetchCities();
  }, [formData.state, authToken]);

  // 🔄 Update Address API Call
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    // for (const key in formData) {
    //   if (!formData[key]) {
    //     setFormError(`Please fill out ${key}`);
    //     return;
    //   }
    // }

    setFormError("");
    setLoading(true);

    const isLoggedIn = authToken && authUserId;

    // 🔍 Lookup country/state/city names
    const countryName =
      countries.find((c) => c.id === parseInt(formData.country))?.name || "";
    const stateName =
      states.find((s) => s.id === parseInt(formData.state))?.name || "";
    const cityName =
      cities.find((c) => c.id === parseInt(formData.city))?.name || "";

    if (isLoggedIn) {
      // 🔐 Logged-in user: Call PUT API
      const payload = {
        user_id: authUserId,
        name: formData.name,
        email: formData.email,
        street: formData.line1,
        apartment_suite: formData.landmark || "",
        zipcode: formData.pincode,
        country_id: formData.country,
        state_id: formData.state,
        city_id: formData.city,
        phone_number: formData.phone_number,
        status: 1,
        address_type: formData.addressType,
      };

      try {
        await axios.put(
          `https://code.gurusport.in/api/user-addresses/${formData.id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        Swal.fire("Success!", "Address updated successfully!", "success");
        onSubmit();
      } catch (error) {
        console.error("❌ Error updating address:", error);
        Swal.fire("Error!", "Failed to update address.", "error");
      }
    } else {
      // 👤 Guest user: Save to localStorage
      const guestPayload = {
        guest_name: formData.name,
        guest_email: formData.email,
        guest_street: formData.line1,
        guest_apartment_suite: formData.landmark,
        guest_zipcode: formData.pincode,

        guest_country_id: formData.country,
        guest_country_name: countryName,

        guest_state_id: formData.state,
        guest_state_name: stateName,

        guest_city_id: formData.city,
        guest_city_name: cityName,

        guest_status: 1,
        guest_address_type: formData.addressType,
        guest_phone_number: formData.phone_number,
      };

      localStorage.setItem("guestAddress", JSON.stringify(guestPayload));
      Swal.fire("Success!", "Guest address updated successfully!", "success");
      onSubmit?.();
    }

    setLoading(false);
  };

  const handleOverlayClick = (e) => {
    if (formRef.current && !formRef.current.contains(e.target)) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-40"
      onClick={handleOverlayClick}
    >
      <div
        ref={formRef}
        className="w-full max-w-[410px] bg-white h-full p-6 shadow-lg overflow-auto relative"
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">Edit Delivery Address</h3>
          <button onClick={onClose}>
            <X />
          </button>
        </div>

        {formError && <p className="text-red-500 text-sm mb-2">{formError}</p>}

        <form onSubmit={handleSubmit} className="space-y-3">
          <label className="block text-sm font-medium mb-1">Full Name</label>
          <input
            type="text"
            placeholder="Full Name"
            className="w-full border px-3 py-2 rounded text-sm"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />

          {/* ✅ Email Field Added */}
          <label className="block text-sm font-medium mb-1">
            Email Address
          </label>
          <input
            type="email"
            placeholder="Email"
            className="w-full border px-3 py-2 rounded text-sm"
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
            required
          />
          {/* Address  */}
          <label className="block text-sm font-medium mb-1">
            Address Line 1
          </label>
          <input
            type="text"
            placeholder="Address Line 1"
            className="w-full border px-3 py-2 rounded text-sm"
            value={formData.line1}
            onChange={(e) =>
              setFormData({ ...formData, line1: e.target.value })
            }
            required
          />
          {/* Country Dropdown */}
          <label className="block text-sm font-medium mb-1">Country</label>
          <select
            className="w-full border px-3 py-2 rounded text-sm"
            value={formData.country}
            onChange={(e) =>
              setFormData({
                ...formData,
                country: e.target.value,
                state: "",
                city: "",
              })
            }
            required
          >
            <option value="">Select Country</option>
            {countries.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {/* State Dropdown */}
          <label className="block text-sm font-medium mb-1">State</label>
          <select
            className="w-full border px-3 py-2 rounded text-sm"
            value={formData.state}
            onChange={(e) =>
              setFormData({ ...formData, state: e.target.value, city: "" })
            }
            required
          >
            <option value="">Select State</option>
            {states.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          {/* City Dropdown */}
          <label className="block text-sm font-medium mb-1">City</label>
          <select
            className="w-full border px-3 py-2 rounded text-sm"
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            required
          >
            <option value="">Select City</option>
            {cities.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          {/* Pincode */}
          <label className="block text-sm font-medium mb-1">Pincode</label>
          <input
            type="text"
            placeholder="Pincode"
            className="w-full border px-3 py-2 rounded text-sm"
            value={formData.pincode}
            onChange={(e) =>
              setFormData({ ...formData, pincode: e.target.value })
            }
            required
          />
          {/* Phone */}
          <label className="block text-sm font-medium mb-1">
            Contact Number
          </label>
          <PhoneInput
            country={"in"}
            inputStyle={{ width: "100%" }}
            value={formData.phone_number}
            onChange={(value) =>
              setFormData({ ...formData, phone_number: value })
            }
            inputProps={{ required: true }}
          />
          {/* Landmark */}
          <label className="block text-sm font-medium mb-1">Landmark</label>
          <input
            type="text"
            placeholder="Landmark"
            className="w-full border px-3 py-2 rounded text-sm"
            value={formData.landmark}
            onChange={(e) =>
              setFormData({ ...formData, landmark: e.target.value })
            }
          />

          <div>
            <label className="block text-sm font-medium mb-1">
              Address Type
            </label>
            <div className="flex gap-4">
              {["Home", "Office"].map((type) => (
                <label key={type} className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="addressType"
                    value={type.toLowerCase()}
                    checked={formData.addressType === type.toLowerCase()}
                    onChange={(e) =>
                      setFormData({ ...formData, addressType: e.target.value })
                    }
                    required
                  />
                  {type}
                </label>
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="bg-red-600 text-white px-5 py-2 rounded text-sm hover:bg-red-700 disabled:opacity-50"
          >
            {loading ? "Updating..." : "UPDATE"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EditAddressForm;
